import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Building2, Mail, Lock, User, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

export default function RegisterPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);

    try {
      await register(email, password, fullName);
      toast.success('Account created successfully');
      navigate('/');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to create account';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-split">
      {/* Left Panel - Hero Image */}
      <div className="login-hero relative">
        <div className="absolute inset-0 bg-slate-900 bg-opacity-70 flex items-center justify-center">
          <div className="text-center text-white p-8">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-orange-500" />
            <h1 className="text-4xl font-black mb-4 font-['Chivo'] tracking-tight">
              APS MODEL VIEWER
            </h1>
            <p className="text-xl text-slate-200 font-light">
              Create your account to start managing 3D model sharing
            </p>
          </div>
          {/* Subtle branding */}
          <div className="absolute bottom-6 left-0 right-0 text-center">
            <p className="text-xs text-slate-400 font-light">
              Built with clarity by{' '}
              <a 
                href="https://thedevguys.in" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-300 hover:text-orange-400 transition-colors underline decoration-slate-500 hover:decoration-orange-400"
              >
                The Dev Guys
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Register Form */}
      <div className="login-form-container bg-slate-50 flex items-center justify-center p-8">
        <div className="technical-form w-full max-w-md">
          <Card className="card border-slate-200">
            <CardHeader className="text-center pb-6">
              <CardTitle className="technical-header text-2xl mb-2">
                CREATE ACCOUNT
              </CardTitle>
              <CardDescription className="technical-subtext">
                Set up your account to manage APS projects and model sharing
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="form-group">
                  <Label htmlFor="fullName" className="form-label">
                    <User className="w-4 h-4 inline mr-2" />
                    FULL NAME
                  </Label>
                  <Input
                    id="fullName"
                    data-testid="fullname-input"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="input-field"
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="email" className="form-label">
                    <Mail className="w-4 h-4 inline mr-2" />
                    EMAIL ADDRESS
                  </Label>
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-field"
                    placeholder="Enter your email address"
                    required
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="password" className="form-label">
                    <Lock className="w-4 h-4 inline mr-2" />
                    PASSWORD
                  </Label>
                  <Input
                    id="password"
                    data-testid="password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field"
                    placeholder="Create a password (min 6 characters)"
                    required
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="confirmPassword" className="form-label">
                    <Lock className="w-4 h-4 inline mr-2" />
                    CONFIRM PASSWORD
                  </Label>
                  <Input
                    id="confirmPassword"
                    data-testid="confirm-password-input"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input-field"
                    placeholder="Confirm your password"
                    required
                  />
                </div>

                {error && (
                  <Alert variant="destructive" className="fade-in">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Button
                  type="submit"
                  data-testid="register-button"
                  disabled={isLoading || !email || !password || !confirmPassword || !fullName}
                  className="btn-primary w-full h-12 text-sm font-semibold"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      CREATING ACCOUNT...
                    </>
                  ) : (
                    'CREATE ACCOUNT'
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="technical-subtext text-sm">
                  Already have an account?{' '}
                  <Link 
                    to="/login" 
                    className="text-orange-500 hover:text-orange-600 font-medium transition-colors"
                    data-testid="login-link"
                  >
                    Sign In
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}